import { useState, type JSX } from 'react';
import { Icon } from './Icon.js';

type Status = 'succeeded' | 'pending' | 'failed' | 'canceled' | 'ok' | 'warn' | string;

const STATUS_MAP: Record<string, { cls: string; label: string }> = {
  succeeded: { cls: 'chip-ok', label: 'succeeded' },
  pending: { cls: 'chip-pending', label: 'pending' },
  failed: { cls: 'chip-fail', label: 'failed' },
  canceled: { cls: 'chip-neutral', label: 'canceled' },
  ok: { cls: 'chip-ok', label: 'подключено' },
  warn: { cls: 'chip-pending', label: 'внимание' },
};

export function StatusChip({ status }: { status: Status }): JSX.Element {
  const c = STATUS_MAP[status] ?? { cls: 'chip-neutral', label: status };
  return <span className={'chip ' + c.cls}>{c.label}</span>;
}

export function CopyButton({
  text,
  label = 'Скопировать',
  size = 'md',
}: {
  text: string;
  label?: string;
  size?: 'sm' | 'md';
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onClick = (): void => {
    navigator.clipboard?.writeText(text).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <button className={'btn ' + (size === 'sm' ? 'btn-sm' : '')} onClick={onClick} type="button">
      <Icon name={copied ? 'check' : 'copy'} size={14} />
      {copied ? 'Скопировано' : label}
    </button>
  );
}

export function CopyCodeBlock({ text }: { text: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = (): void => {
    navigator.clipboard?.writeText(text).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onCopy}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCopy();
        }
      }}
      title={copied ? 'Скопировано' : 'Нажмите, чтобы скопировать'}
      style={{ position: 'relative', cursor: 'pointer' }}
    >
      <pre
        className="code"
        style={{ paddingRight: 44, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
      >
        {text}
      </pre>
      <span
        aria-hidden
        style={{
          position: 'absolute',
          right: 10,
          top: '50%',
          transform: 'translateY(-50%)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: 6,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          color: copied ? 'var(--accent)' : 'var(--fg-muted)',
          pointerEvents: 'none',
        }}
      >
        <Icon name={copied ? 'check' : 'copy'} size={14} />
      </span>
    </div>
  );
}

export function JsonView({ data }: { data: unknown }): JSX.Element {
  const str = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  const tokens: Array<{ t: string; v: string }> = [];
  const regex = /("(?:[^"\\]|\\.)*"\s*:)|("(?:[^"\\]|\\.)*")|(-?\d+\.?\d*)|(true|false|null)|([{}\[\],])/g;
  let last = 0;
  let m;
  while ((m = regex.exec(str)) !== null) {
    if (m.index > last) tokens.push({ t: 'txt', v: str.slice(last, m.index) });
    if (m[1]) tokens.push({ t: 'key', v: m[1] });
    else if (m[2]) tokens.push({ t: 'str', v: m[2] });
    else if (m[3]) tokens.push({ t: 'num', v: m[3] });
    else if (m[4]) tokens.push({ t: 'num', v: m[4] });
    else if (m[5]) tokens.push({ t: 'punc', v: m[5] });
    last = regex.lastIndex;
  }
  if (last < str.length) tokens.push({ t: 'txt', v: str.slice(last) });
  return (
    <pre className="code">
      {tokens.map((tk, i) =>
        tk.t === 'txt' ? (
          <span key={i}>{tk.v}</span>
        ) : (
          <span key={i} className={tk.t}>
            {tk.v}
          </span>
        ),
      )}
    </pre>
  );
}

export function Sparkline({
  data,
  color = 'var(--fg)',
  w = 120,
  h = 34,
}: {
  data: number[];
  color?: string;
  w?: number;
  h?: number;
}): JSX.Element {
  const max = Math.max(...data, 1);
  const pts: Array<[number, number]> = data.map((v, i) => [
    (i / (data.length - 1)) * w,
    h - (v / max) * (h - 4) - 2,
  ]);
  const d = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const area = d + ` L ${w} ${h} L 0 ${h} Z`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
      <path d={area} fill={color} opacity="0.08" />
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Switch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={checked}
      className={'switch ' + (checked ? 'on' : '')}
      onClick={() => !disabled && onChange?.(!checked)}
      style={{ opacity: disabled ? 0.5 : 1 }}
    />
  );
}
